import React, { useEffect, useState } from 'react'
import {
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Switch,
  Typography,
  Alert,
  Tooltip,
  Divider,
  Modal,
  theme
} from 'antd'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCreditCard,
  faBuilding,
  faInfoCircle,
  faExclamationTriangle,
  faCheckCircle,
  faMoneyBill,
  faClipboardList,
  faMoneyBillTransfer
} from '@fortawesome/free-solid-svg-icons'
import { ContainerCard } from '../../../reusable-components/AgiliteCards'
import PageHeader from '../../../reusable-components/PageHeader'
import { hexToRGBA } from '../../../lib/utils'
import useBillingProfile from '../hooks/useBillingProfile'
import FloatingFormControlActions from '../../../reusable-components/FloatingFormControlActions'
import { DISCIPLINE_TYPES } from '../utils'

const { Title, Text, Paragraph } = Typography

const BillingProfile = ({
  clinicId,
  practiceName,
  handleTabChange,
  billingProfileForm,
  handleValuesChange,
  handleFormSubmit,
  recordData,
  newRecord
}) => {
  const { token } = theme.useToken()
  const [initialValuesControl, setInitialValuesControl] = useState(recordData?.billingProfile || {})

  useEffect(() => {
    if (billingProfileForm) {
      billingProfileForm.setFieldsValue(recordData?.billingProfile || {})
    }
    setInitialValuesControl(recordData?.billingProfile || {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordData])

  // ======== STANDALONE PAGE FORM EXCEPTION ======== //
  const [standaloneForm] = Form.useForm()
  // ======== STANDALONE PAGE FORM EXCEPTION ======== //

  // ======== CLINIC FORM EXCEPTION ======== //
  const hasInheritedClinic = Boolean(clinicId) || newRecord
  // Check if we have an inherited clinic ID (automatically selected)
  // This component is used in the clinics form, so we need to check if the clinicId is present
  // If the clinicID is not present, then we are in the clinics form and we need to show the facility selection
  // If the clinic Id is not present we will need to go and fetch the billing profile data for the facility
  // ======== CLINIC FORM EXCEPTION ======== //

  const targetForm = billingProfileForm || standaloneForm

  const {
    clinicsLoading,
    schemesLoading,
    schemeOps,
    selectedClinic,
    clinicOptions,
    handleFacilityChange,
    standaloneFormSubmit,
    unsavedChanges,
    setUnsavedChanges
  } = useBillingProfile({
    clinicRef: clinicId,
    practiceName,
    handleTabChange,
    targetForm,
    setInitialValuesControl,
    initialValuesControl,
    newRecord
  })

  const formItemLayout = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 }
  }

  // TODO: Move this to the theme provider
  // Enhanced token colors for consistent styling

  return (
    <ContainerCard bodyStyle={{ padding: 0 }}>
      {/* ======== CLINIC FORM EXCEPTION ======== // */}
      {/* Only show the page header if we are not in the clinic form as the clinic form has its own page header */}
      {hasInheritedClinic ? <></> : <PageHeader title='Facility Billing Profiles' icon={faCreditCard} />}
      {/* ======== CLINIC FORM EXCEPTION ======== // */}

      <div style={{ padding: '24px' }}>
        {/* Enhanced Facility Selector Section - Only show when no inherited clinic ID */}
        {!hasInheritedClinic && (
          <Card
            title={
              <Space size={12}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    backgroundColor: hexToRGBA(token.colorPrimary, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FontAwesomeIcon icon={faBuilding} style={{ color: token.colorPrimary, fontSize: '18px' }} />
                </div>
                <div>
                  <Text strong style={{ fontSize: '18px', display: 'block' }}>
                    Facility Selection
                  </Text>
                  <Text type='secondary' style={{ fontSize: '14px' }}>
                    Choose the facility to configure billing profile for
                  </Text>
                </div>
              </Space>
            }
            bordered={false}
            style={{
              marginBottom: '32px',
              borderRadius: '16px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
              border: `1px solid ${hexToRGBA(token.colorBorder, 0.1)}`
            }}
            bodyStyle={{ padding: '32px' }}
            headStyle={{
              borderBottom: 'none',
              backgroundColor: 'transparent',
              padding: '24px 32px 0 32px'
            }}
          >
            <Row gutter={[24, 24]} align='middle'>
              <Col xs={24} lg={16}>
                <Form.Item
                  label={
                    <Space>
                      <Text strong style={{ fontSize: '15px' }}>
                        Facility
                      </Text>
                      <Tooltip title='Select the facility to manage billing profile for. Each facility can have its own billing configuration.'>
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          style={{ color: token.colorPrimary, fontSize: '12px', cursor: 'help' }}
                        />
                      </Tooltip>
                    </Space>
                  }
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    size='large'
                    placeholder='Select a facility to configure billing profile'
                    value={selectedClinic}
                    onChange={(value) => {
                      handleFacilityChange(value)
                    }}
                    loading={clinicsLoading}
                    options={clinicOptions}
                    showSearch
                    filterOption={(input, option) => option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    style={{
                      width: '100%',
                      borderRadius: '8px'
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} lg={8}>
                {selectedClinic && (
                  <Alert
                    message='Facility Profile Selected'
                    description='Ready to configure billing profile for the selected facility'
                    type='info'
                    showIcon
                    icon={<FontAwesomeIcon icon={faCheckCircle} />}
                    style={{
                      borderRadius: '8px',
                      marginBottom: 0,
                      border: `1px solid ${hexToRGBA(token.colorInfo, 0.3)}`
                    }}
                  />
                )}
              </Col>
            </Row>
          </Card>
        )}

        {selectedClinic || hasInheritedClinic || newRecord ? (
          <Form
            layout='vertical'
            form={targetForm}
            initialValues={
              // ======== CLINIC FORM EXCEPTION ======== //
              // Don't used passed recordData when the billing profile is opened as a standalone page
              initialValuesControl ? { ...initialValuesControl } : {}
              // ======== CLINIC FORM EXCEPTION ======== //
            }
            {...formItemLayout}
            onValuesChange={(values, fullForm, key) => {
              if (values.disciplineType) {
                let disciplinePrefix = 0
                disciplinePrefix = DISCIPLINE_TYPES.find((type) => type.value === values.disciplineType)?.prefix
                if (handleValuesChange) {
                  handleValuesChange({ billingProfile: { ...fullForm, disciplinePrefix } })
                } else {
                  setUnsavedChanges(true)
                }
              } else {
                if (handleValuesChange) {
                  handleValuesChange({ billingProfile: fullForm })
                } else {
                  setUnsavedChanges(true)
                }
              }
            }}
            onFinish={(values) => {
              const payload = {
                billingProfile: {
                  ...values
                }
              }
              if (hasInheritedClinic) {
                handleFormSubmit(payload)
              } else {
                standaloneFormSubmit(payload)
              }
            }}
            onFinishFailed={(errorInfo) => {
              Modal.error({
                title: 'Form Validation Error',
                content: errorInfo.errorFields[0].errors[0]
              })
            }}
          >
            <Row gutter={[32, 32]}>
              {/* Practice Information Column */}
              <Col xs={24} lg={12}>
                <Card
                  title={
                    <Space size={12}>
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          backgroundColor: hexToRGBA(token.colorPrimary, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <FontAwesomeIcon icon={faBuilding} style={{ color: token.colorPrimary, fontSize: '18px' }} />
                      </div>
                      <div>
                        <Text strong style={{ fontSize: '18px', display: 'block' }}>
                          Practice Configuration
                        </Text>
                        <Text type='secondary' style={{ fontSize: '14px' }}>
                          Configure billing practice details
                        </Text>
                      </div>
                    </Space>
                  }
                  bordered={false}
                  style={{
                    borderRadius: '16px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f0ff 100%)',
                    border: `1px solid ${hexToRGBA(token.colorBorder, 0.1)}`,
                    height: '100%'
                  }}
                  bodyStyle={{ padding: '32px' }}
                  headStyle={{
                    borderBottom: 'none',
                    backgroundColor: 'transparent',
                    padding: '24px 32px 0 32px'
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
                    <div>
                      <Title level={5} style={{ margin: '0 0 20px 0', color: token.colorPrimary }}>
                        <FontAwesomeIcon icon={faMoneyBillTransfer} style={{ marginRight: '8px' }} />
                        Switching
                      </Title>

                      <Paragraph style={{ marginBottom: '24px', color: token.colorTextSecondary }}>
                        Enable Switching for this facility.
                      </Paragraph>

                      <Form.Item name='switchingEnabled' valuePropName='checked' style={{ margin: 0 }}>
                        <Switch checkedChildren='Yes' unCheckedChildren='No' />
                      </Form.Item>
                    </div>
                    <div>
                      <Title level={5} style={{ margin: '0 0 20px 0', color: token.colorPrimary }}>
                        <FontAwesomeIcon icon={faBuilding} style={{ marginRight: '8px' }} />
                        Practice Details
                      </Title>

                      <Paragraph style={{ marginBottom: '24px', color: token.colorTextSecondary }}>
                        Configure the billing practice information for this facility. This determines how billing codes
                        and rates are applied.
                      </Paragraph>

                      {/* Discipline Type */}
                      <Form.Item
                        name='disciplineType'
                        label={
                          <Space>
                            <Text strong style={{ fontSize: '15px' }}>
                              Discipline Type
                            </Text>
                            <Tooltip title='Medical discipline for billing classification'>
                              <FontAwesomeIcon
                                icon={faInfoCircle}
                                style={{ color: token.colorPrimary, fontSize: '12px', cursor: 'help' }}
                              />
                            </Tooltip>
                          </Space>
                        }
                        rules={[
                          {
                            required: true,
                            message:
                              'Please select your medical discipline type. This determines the billing codes and rates that will be applied to your services and affects insurance reimbursements.'
                          }
                        ]}
                        style={{ marginBottom: '24px' }}
                      >
                        <Select
                          size='large'
                          placeholder='Select discipline type'
                          options={[{ label: '- Select Discipline Type -', value: '' }, ...DISCIPLINE_TYPES]}
                          style={{
                            borderRadius: '8px'
                          }}
                        />
                      </Form.Item>

                      {/* Discipline Prefix */}
                      <Form.Item
                        name='disciplinePrefix'
                        label={
                          <Space>
                            <Text strong style={{ fontSize: '15px' }}>
                              Discipline Prefix
                            </Text>
                            <Tooltip title='Billing code prefix for this discipline'>
                              <FontAwesomeIcon
                                icon={faInfoCircle}
                                style={{ color: token.colorPrimary, fontSize: '12px', cursor: 'help' }}
                              />
                            </Tooltip>
                          </Space>
                        }
                        style={{ marginBottom: '24px' }}
                      >
                        <Input
                          size='large'
                          readOnly
                          style={{
                            borderRadius: '8px',
                            fontSize: '16px',
                            padding: '12px 16px',
                            backgroundColor: hexToRGBA(token.colorBgContainer, 0.5)
                          }}
                        />
                      </Form.Item>

                      {/* Practice Rate */}
                      <Form.Item
                        name='roomsDefault'
                        label={
                          <Space>
                            <Text strong style={{ fontSize: '15px' }}>
                              Practice Rate - Rooms: Default Base Rate or %
                            </Text>
                            <Tooltip title='Default base rate or percentage for room charges'>
                              <FontAwesomeIcon
                                icon={faInfoCircle}
                                style={{ color: token.colorPrimary, fontSize: '12px', cursor: 'help' }}
                              />
                            </Tooltip>
                          </Space>
                        }
                      >
                        <Input
                          size='large'
                          placeholder='Enter default base rate or percentage'
                          style={{
                            borderRadius: '8px',
                            fontSize: '16px',
                            padding: '12px 16px'
                          }}
                        />
                      </Form.Item>
                    </div>

                    <Divider style={{ margin: '8px 0', borderColor: hexToRGBA(token.colorBorder, 0.3) }} />

                    {/* Practice Options */}
                    <div>
                      <Title level={5} style={{ margin: '0 0 20px 0', color: token.colorPrimary }}>
                        <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: '8px' }} />
                        Practice Options
                      </Title>

                      <Row gutter={[24, 16]}>
                        <Col xs={24} sm={8}>
                          <Card
                            size='small'
                            style={{
                              borderRadius: '12px',
                              border: `2px solid ${hexToRGBA(token.colorInfo, 0.15)}`,
                              backgroundColor: hexToRGBA(token.colorInfo, 0.04),
                              textAlign: 'center',
                              height: '100px'
                            }}
                            bodyStyle={{ padding: '16px' }}
                          >
                            <Text strong style={{ display: 'block', marginBottom: '12px', fontSize: '14px' }}>
                              Network
                            </Text>
                            <Form.Item name='network' valuePropName='checked' style={{ margin: 0 }}>
                              <Switch checkedChildren='Yes' unCheckedChildren='No' />
                            </Form.Item>
                          </Card>
                        </Col>

                        <Col xs={24} sm={8}>
                          <Card
                            size='small'
                            style={{
                              borderRadius: '12px',
                              border: `2px solid ${hexToRGBA(token.colorSuccess, 0.15)}`,
                              backgroundColor: hexToRGBA(token.colorSuccess, 0.04),
                              textAlign: 'center',
                              height: '100px'
                            }}
                            bodyStyle={{ padding: '16px' }}
                          >
                            <Text strong style={{ display: 'block', marginBottom: '12px', fontSize: '14px' }}>
                              IPA
                            </Text>
                            <Form.Item name='ipa' valuePropName='checked' style={{ margin: 0 }}>
                              <Switch checkedChildren='Yes' unCheckedChildren='No' />
                            </Form.Item>
                          </Card>
                        </Col>

                        <Col xs={24} sm={8}>
                          <Card
                            size='small'
                            style={{
                              borderRadius: '12px',
                              border: `2px solid ${hexToRGBA(token.colorWarning, 0.15)}`,
                              backgroundColor: hexToRGBA(token.colorWarning, 0.04),
                              textAlign: 'center',
                              height: '100px'
                            }}
                            bodyStyle={{ padding: '16px' }}
                          >
                            <Text strong style={{ display: 'block', marginBottom: '12px', fontSize: '14px' }}>
                              Dispensing
                            </Text>
                            <Form.Item name='dispensing' valuePropName='checked' style={{ margin: 0 }}>
                              <Switch checkedChildren='Yes' unCheckedChildren='No' />
                            </Form.Item>
                          </Card>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </Card>
              </Col>

              {/* Scheme Contracts Column */}
              <Col xs={24} lg={12}>
                <Card
                  title={
                    <Space size={12}>
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          backgroundColor: hexToRGBA(token.colorSuccess, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faClipboardList}
                          style={{ color: token.colorSuccess, fontSize: '18px' }}
                        />
                      </div>
                      <div>
                        <Text strong style={{ fontSize: '18px', display: 'block' }}>
                          Scheme Contracts
                        </Text>
                        <Text type='secondary' style={{ fontSize: '14px' }}>
                          Medical aid schemes and contracts
                        </Text>
                      </div>
                    </Space>
                  }
                  bordered={false}
                  loading={schemesLoading}
                  style={{
                    borderRadius: '16px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f0fff4 100%)',
                    border: `1px solid ${hexToRGBA(token.colorBorder, 0.1)}`,
                    height: '100%'
                  }}
                  bodyStyle={{ padding: '32px' }}
                  headStyle={{
                    borderBottom: 'none',
                    backgroundColor: 'transparent',
                    padding: '24px 32px 0 32px'
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
                    <div>
                      <Title level={5} style={{ margin: '0 0 20px 0', color: token.colorSuccess }}>
                        <FontAwesomeIcon icon={faMoneyBill} style={{ marginRight: '8px' }} />
                        Contracted Schemes
                      </Title>

                      <Paragraph style={{ marginBottom: '24px', color: token.colorTextSecondary }}>
                        Select the medical aid schemes that this facility has contracts with. This determines billing
                        rates and coverage options.
                      </Paragraph>

                      <Form.Item
                        name='schemeContracts'
                        label={
                          <Space>
                            <Text strong style={{ fontSize: '15px' }}>
                              Contracted In Schemes
                            </Text>
                            <Tooltip title='Medical aid schemes this facility is contracted with'>
                              <FontAwesomeIcon
                                icon={faInfoCircle}
                                style={{ color: token.colorSuccess, fontSize: '12px', cursor: 'help' }}
                              />
                            </Tooltip>
                          </Space>
                        }
                        rules={[
                          {
                            required: true,
                            message:
                              'Please select at least one medical aid scheme that you have contracts with. This ensures proper billing rates and coverage verification for your patients.'
                          }
                        ]}
                      >
                        <Select
                          showSearch
                          mode='multiple'
                          size='large'
                          placeholder='Select contracted schemes'
                          filterOption={(input, option) => option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                          options={schemeOps.map((scheme) => {
                            return { label: `${scheme.name} (${scheme.code})`, value: scheme.code }
                          })}
                          style={{ width: '100%' }}
                          tagRender={(props) => (
                            <div
                              style={{
                                display: 'inline-block',
                                padding: '4px 8px',
                                margin: '2px',
                                borderRadius: '6px',
                                background: hexToRGBA(token.colorSuccess, 0.1),
                                border: `1px solid ${hexToRGBA(token.colorSuccess, 0.3)}`,
                                fontSize: '12px',
                                lineHeight: '16px',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {props.label}
                              <span style={{ marginLeft: '4px', cursor: 'pointer' }} onClick={props.onClose}>
                                ✕
                              </span>
                            </div>
                          )}
                        />
                      </Form.Item>
                    </div>

                    <Divider style={{ margin: '8px 0', borderColor: hexToRGBA(token.colorBorder, 0.3) }} />

                    {/* Enhanced Alert Components */}
                    <Alert
                      message='Billing Configuration'
                      description='These settings determine how billing is processed for this facility. Ensure all contracted schemes are selected for accurate billing and claims processing.'
                      type='info'
                      showIcon
                      icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
                      style={{
                        borderRadius: '8px',
                        border: `1px solid ${hexToRGBA(token.colorInfo, 0.3)}`
                      }}
                    />

                    <Alert
                      message='Practice Integration'
                      description='Billing profile settings will be integrated with the facility workflow for seamless billing operations.'
                      type='success'
                      showIcon
                      icon={<FontAwesomeIcon icon={faCheckCircle} />}
                      style={{
                        borderRadius: '8px',
                        border: `1px solid ${hexToRGBA(token.colorSuccess, 0.3)}`
                      }}
                    />
                  </div>
                </Card>
              </Col>
            </Row>
          </Form>
        ) : null}

        {/* ======== STANDALONE PAGE FORM EXCEPTION ======== // */}
        {/* Clinic form has its own control actions */}
        {!hasInheritedClinic && (
          <FloatingFormControlActions
            saveActionText='Save Changes'
            show={unsavedChanges}
            formResetAction={() => {
              targetForm.resetFields()
              setUnsavedChanges(false)
            }}
            formSaveAction={() => {
              targetForm.submit()
            }}
          />
        )}
        {/* ======== STANDALONE PAGE FORM EXCEPTION ======== // */}
      </div>
    </ContainerCard>
  )
}

export default BillingProfile
